import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { SidebarMenu, SidebarMenuItem } from "@/components/ui/sidebar";
import { Bug, LogOut, Sun } from "lucide-react";
import { PropTypes } from "prop-types";
import { useCallback, useEffect, useRef, useState } from "react";
import { useUser } from "../../context/user-context";
import { extractUserDetails } from "../../context/user-util";
import { SidebarFooter, SidebarMenuButton } from "../ui/sidebar";
import { Skeleton } from "../ui/skeleton";

const VizSidebarFooter = ({ isMobile }) => {
  const { user, signOut } = useUser();
  const [userDetails, setUserDetails] = useState(null);
  const [showBugJoke, setShowBugJoke] = useState(false);
  const bugTimerRef = useRef(null);

  const handleLightMode = useCallback(() => {
    setShowBugJoke(true);
    clearTimeout(bugTimerRef.current);
    bugTimerRef.current = setTimeout(() => setShowBugJoke(false), 3000);
  }, []);

  useEffect(() => {
    if (user) {
      setUserDetails(extractUserDetails(user));
    }
  }, [user]);

  return (
    <SidebarFooter>
      <SidebarMenu>
        {userDetails ? (
          <SidebarMenuItem>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <SidebarMenuButton
                  className={`
                  m-auto
                  bg-muted
                  flex 
                `}
                  size="lg"
                >
                  <Avatar className="h-8 w-8 rounded-lg">
                    <AvatarImage src={userDetails.avatarUrl} alt="@shadcn" />
                    <AvatarFallback>{userDetails.initials}</AvatarFallback>
                  </Avatar>
                  <span className="ml-2 text-center hover:text-white hover:font-bold">
                    {userDetails.fullName?.split(" ")[0]}
                  </span>
                </SidebarMenuButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg ml-4"
                side={isMobile ? "bottom" : "right"}
                align="end"
                sideOffset={4}
              >
                <DropdownMenuLabel className="p-1 font-normal">
                  <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                    <Avatar className="h-8 w-8 rounded-lg">
                      <AvatarImage src={userDetails.avatarUrl} alt={"Vladut"} />
                      <AvatarFallback className="rounded-lg">
                        {userDetails.initials}
                      </AvatarFallback>
                    </Avatar>
                    <div className="grid flex-1 text-left text-sm leading-tight">
                      <span className="truncate font-semibold">
                        {userDetails.fullName}
                      </span>
                      <span className="truncate text-xs">
                        {userDetails.email}
                      </span>
                    </div>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuGroup>
                  <DropdownMenuItem onSelect={(e) => { e.preventDefault(); handleLightMode(); }}>
                    {showBugJoke ? (
                      <>
                        <Bug className="size-4 mr-1 animate-pulse" />
                        <span className="text-xs">Nope! Bugs are attracted to light</span>
                      </>
                    ) : (
                      <>
                        <Sun className="size-4 mr-1" />
                        Light Mode
                      </>
                    )}
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={signOut}>
                    <LogOut className="size-4 mr-1" />
                    Log out
                  </DropdownMenuItem>
                </DropdownMenuGroup>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        ) : (
          <Skeleton className="bg-gray-700 h-10 w-10 rounded-full" />
        )}

        <span
          className="mt-4 text-xs text-center text-muted-foreground opacity-60"
          aria-label="Version"
        >
          v0.0.1
        </span>
      </SidebarMenu>
    </SidebarFooter>
  );
};
VizSidebarFooter.propTypes = {
  isMobile: PropTypes.bool.isRequired,
};

export default VizSidebarFooter;
